/**
 * Curl 命令解析器主类
 * 负责将 curl 命令解析为结构化数据
 */

import { CurlTokenizer } from './tokenizer.js';
import { CurlOptionParser } from './optionParser.js';

export class CurlParser {
  constructor() {
    this.tokenizer = new CurlTokenizer();
    this.optionParser = new CurlOptionParser();
  }

  /**
   * 解析 curl 命令
   * @param {string} curlCommand - curl 命令字符串
   * @returns {Object} 解析结果
   */
  parse(curlCommand) {
    try {
      // 验证输入
      if (!curlCommand || typeof curlCommand !== 'string') {
        throw new Error('curl 命令不能为空');
      }

      // 预处理命令
      const cleanCommand = this.preprocessCommand(curlCommand);

      // 词法分析
      const tokens = this.tokenizer.tokenize(cleanCommand);

      // 语法分析
      const parsed = this.parseTokens(tokens);

      // 后处理和验证
      const result = this.postProcess(parsed);

      // 添加元信息
      result.metadata = {
        originalCommand: curlCommand,
        parsedAt: new Date().toISOString(),
        version: '1.0.0'
      };

      return result;

    } catch (error) {
      throw new Error(`解析失败: ${error.message}`);
    }
  }

  /**
   * 预处理 curl 命令
   * @param {string} command - 原始命令
   * @returns {string} 处理后的命令
   */
  preprocessCommand(command) {
    return command
      .trim()
      // 处理多行命令
      .replace(/\\\s*\n/g, ' ')
      // 标准化空白字符
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * 解析 token 数组
   * @param {Array} tokens - token 数组
   * @returns {Object} 解析结果
   */
  parseTokens(tokens) {
    const result = this.createEmptyResult();
    let i = 0;

    // 跳过 'curl' 命令本身
    if (tokens[i] && tokens[i].value === 'curl') {
      i++;
    }

    // 解析所有选项和参数
    while (i < tokens.length) {
      const token = tokens[i];

      if (token.type === 'option') {
        const optionResult = this.parseOption(token, tokens.slice(i + 1));
        this.applyOption(result, optionResult);
        i += optionResult.consumedTokens + 1;
      } else if (token.type === 'url' || (!result.url && token.type === 'string')) {
        result.url = token.value;
        i++;
      } else {
        // 跳过未知 token
        i++;
      }
    }

    return result;
  }

  /**
   * 解析单个选项
   * @param {Object} token - 选项 token
   * @param {Array} args - 参数 tokens
   * @returns {Object} 解析结果
   */
  parseOption(token, args) {
    try {
      return this.optionParser.parseOption(token, args);
    } catch (error) {
      throw new Error(`选项 ${token.value} 解析失败: ${error.message}`);
    }
  }

  /**
   * 应用解析后的选项到结果
   * @param {Object} result - 结果对象
   * @param {Object} optionResult - 选项解析结果
   */
  applyOption(result, optionResult) {
    const { option, value } = optionResult;

    switch (option) {
      case 'request':
        result.method = value;
        break;

      case 'header':
        this.applyHeader(result, value);
        break;

      case 'data':
      case 'data-raw':
      case 'data-binary':
      case 'data-urlencode':
        this.applyData(result, value);
        break;

      case 'user':
        this.applyAuth(result, value);
        break;

      case 'basic':
      case 'digest':
      case 'oauth2-bearer':
        this.applyAuthType(result, option, value);
        break;

      case 'cookie':
        this.applyCookies(result, value);
        break;

      case 'cookie-jar':
        result.cookieJar = value;
        break;

      case 'form':
      case 'form-string':
        this.applyForm(result, value);
        break;

      case 'proxy':
        result.proxy = value;
        break;

      case 'proxy-user':
        this.applyProxyAuth(result, value);
        break;

      case 'insecure':
        result.insecure = value;
        break;

      case 'cert':
        result.cert = value;
        break;

      case 'key':
        result.key = value;
        break;

      case 'cacert':
        result.cacert = value;
        break;

      case 'location':
        result.followRedirects = value;
        break;

      case 'max-redirs':
        result.maxRedirects = value;
        break;

      case 'connect-timeout':
        result.connectTimeout = value;
        break;

      case 'max-time':
        result.maxTime = value;
        break;

      case 'speed-limit':
        result.speedLimit = value;
        break;

      case 'speed-time':
        result.speedTime = value;
        break;

      case 'output':
        result.output = value;
        break;

      case 'remote-name':
        result.remoteName = true;
        break;

      case 'silent':
        result.silent = true;
        break;

      case 'verbose':
        result.verbose = true;
        break;

      case 'user-agent':
        result.headers['User-Agent'] = value;
        break;

      case 'referer':
        result.headers['Referer'] = value;
        break;

      case 'compressed':
        result.compressed = true;
        break;

      case 'keepalive-time':
        result.keepaliveTime = value;
        break;

      default:
        // 存储未知选项
        if (!result.unknownOptions) {
          result.unknownOptions = [];
        }
        result.unknownOptions.push(optionResult);
    }
  }

  /**
   * 应用请求头
   * @param {Object} result - 结果对象
   * @param {Object} header - 请求头对象
   */
  applyHeader(result, header) {
    result.headers[header.name] = header.value;
  }

  /**
   * 应用请求数据
   * @param {Object} result - 结果对象
   * @param {Object} data - 数据对象
   */
  applyData(result, data) {
    if (result.data.type !== 'none') {
      // 如果已有数据，合并或报错
      throw new Error('不能同时指定多个数据选项');
    }

    result.data = data;
  }

  /**
   * 应用认证信息
   * @param {Object} result - 结果对象
   * @param {Object} auth - 认证对象
   */
  applyAuth(result, auth) {
    result.auth = {
      type: 'basic',
      ...auth
    };
  }

  /**
   * 应用认证类型
   * @param {Object} result - 结果对象
   * @param {string} type - 认证类型
   * @param {any} value - 认证值
   */
  applyAuthType(result, type, value) {
    if (type === 'oauth2-bearer') {
      result.auth = {
        type: 'bearer',
        token: value
      };
    } else {
      result.auth = {
        type: type,
        ...value
      };
    }
  }

  /**
   * 应用 Cookie
   * @param {Object} result - 结果对象
   * @param {Object} cookies - Cookie 对象
   */
  applyCookies(result, cookies) {
    result.cookies = { ...result.cookies, ...cookies };
  }

  /**
   * 应用表单数据
   * @param {Object} result - 结果对象
   * @param {Object} form - 表单对象
   */
  applyForm(result, form) {
    if (!result.form) {
      result.form = [];
    }
    result.form.push(form);
  }

  /**
   * 应用代理认证
   * @param {Object} result - 结果对象
   * @param {Object} proxyAuth - 代理认证对象
   */
  applyProxyAuth(result, proxyAuth) {
    result.proxyAuth = proxyAuth;
  }

  /**
   * 创建空的解析结果
   * @returns {Object} 空结果对象
   */
  createEmptyResult() {
    return {
      url: '',
      method: 'GET',
      headers: {},
      data: {
        type: 'none',
        content: ''
      },
      auth: {
        type: 'none'
      },
      cookies: {},
      form: null,
      proxy: null,
      insecure: false,
      followRedirects: false,
      connectTimeout: 0,
      maxTime: 0,
      userAgent: '',
      output: null,
      silent: false,
      verbose: false,
      compressed: false
    };
  }

  /**
   * 后处理解析结果
   * @param {Object} result - 解析结果
   * @returns {Object} 处理后的结果
   */
  postProcess(result) {
    // 如果有数据但方法仍是 GET，改为 POST
    if (result.data.type !== 'none' && result.method === 'GET') {
      result.method = 'POST';
    }

    // 如果有 JSON 数据但没有 Content-Type，自动添加
    if (result.data.type === 'data' && this.isJsonData(result.data.content)) {
      if (!result.headers['Content-Type']) {
        result.headers['Content-Type'] = 'application/json';
      }
      result.data.type = 'json';
    }

    // 如果有表单数据但没有 Content-Type，自动添加
    if (result.form && result.form.length > 0) {
      if (!result.headers['Content-Type']) {
        result.headers['Content-Type'] = 'multipart/form-data';
      }
    }

    // 处理认证信息
    if (result.auth.type === 'user') {
      // 检查是否已有 Authorization 头
      if (!result.headers['Authorization']) {
        // 转换为 Basic 认证头
        const credentials = btoa(`${result.auth.username}:${result.auth.password || ''}`);
        result.headers['Authorization'] = `Basic ${credentials}`;
        result.auth.type = 'basic';
      }
    }

    return result;
  }

  /**
   * 检查是否为 JSON 数据
   * @param {string} content - 内容
   * @returns {boolean} 是否为 JSON
   */
  isJsonData(content) {
    try {
      JSON.parse(content);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * 验证解析结果
   * @param {Object} result - 解析结果
   * @returns {Object} 验证结果
   */
  validate(result) {
    const errors = [];
    const warnings = [];

    // 检查 URL
    if (!result.url) {
      errors.push('缺少 URL 参数');
    } else if (!this.isValidUrl(result.url)) {
      warnings.push('URL 格式可能不正确');
    }

    // 检查数据和方法的一致性
    if (result.data.type !== 'none' && result.method === 'GET') {
      warnings.push('GET 请求通常不应该包含请求体数据');
    }

    // 检查认证信息
    if (result.auth.type !== 'none' && !result.headers['Authorization']) {
      warnings.push('缺少 Authorization 请求头');
    }

    // 检查 SSL 选项
    if (result.insecure && !result.url.startsWith('https://')) {
      warnings.push('--insecure 选项对 HTTP URL 无效');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * 验证 URL 格式
   * @param {string} url - URL 字符串
   * @returns {boolean} 是否有效
   */
  isValidUrl(url) {
    try {
      new URL(url);
      return true;
    } catch {
      // 检查相对 URL
      return /^https?:\/\//i.test(url);
    }
  }

  /**
   * 获取解析器的调试信息
   * @param {string} command - curl 命令
   * @returns {Object} 调试信息
   */
  getDebugInfo(command) {
    const tokens = this.tokenizer.tokenize(command);
    return {
      command,
      tokens,
      tokenizerDebug: this.tokenizer.getDebugInfo()
    };
  }

  /**
   * 获取支持的选项列表
   * @returns {Array} 选项列表
   */
  getSupportedOptions() {
    return this.optionParser.getSupportedOptions();
  }

  /**
   * 获取选项描述
   * @param {string} option - 选项名称
   * @returns {string} 描述
   */
  getOptionDescription(option) {
    return this.optionParser.getOptionDescription(option);
  }
}